import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';

/**
 * A reusable line chart component for visualizing time-series data
 * @param {Object} props
 * @param {Array} props.data - Array of data points
 * @param {string} props.title - Chart title
 * @param {string} props.xLabel - Label for x-axis
 * @param {string} props.yLabel - Label for y-axis
 * @param {string} props.lineColor - Color for the line
 * @param {string} props.height - Chart height (CSS value)
 * @param {boolean} props.formatXAxis - Whether to format x-axis as time (for solar profile)
 */
const LineChartBasic = ({ 
  data, 
  title, 
  xLabel, 
  yLabel, 
  lineColor = '#5E5ADE', 
  height = '200px',
  formatXAxis = false
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    // Clean up previous chart instance if it exists
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    if (!chartRef.current || !data || data.length === 0) return;

    const ctx = chartRef.current.getContext('2d');
    
    // If data is an object with keys, convert to array format
    let chartData = data;
    if (!Array.isArray(data) && typeof data === 'object') {
      chartData = Object.entries(data).map(([hour, value]) => ({
        x: parseInt(hour),
        y: typeof value === 'number' ? value : 0
      })).sort((a, b) => a.x - b.x);
    }

    // Extract x and y values
    const xValues = chartData.map(item => 
      typeof item === 'object' && 'x' in item ? item.x : 
      Array.isArray(item) ? item[0] : null
    );
    
    const yValues = chartData.map(item => 
      typeof item === 'object' && 'y' in item ? item.y : 
      Array.isArray(item) ? item[1] : 
      typeof item === 'number' ? item : null
    );

    // Format x-axis labels as time if requested
    let xLabels = xValues.some(x => x !== null) ? xValues : Array.from({ length: yValues.length }, (_, i) => i);
    
    if (formatXAxis) {
      xLabels = xLabels.map(hour => {
        // Format as 12-hour time (e.g., "12 AM", "1 PM", etc.)
        const formattedHour = hour % 12 || 12;
        const amPm = hour < 12 ? 'AM' : 'PM';
        return `${formattedHour} ${amPm}`;
      });
    }

    // Create chart
    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: xLabels,
        datasets: [{
          label: title,
          data: yValues,
          borderColor: lineColor,
          backgroundColor: `${lineColor}33`, // Add transparency
          borderWidth: 2,
          fill: true,
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 5
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: !!title,
            text: title,
            font: {
              size: 14,
              weight: 'bold'
            }
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              title: function(tooltipItems) {
                if (formatXAxis) {
                  const hour = tooltipItems[0].dataIndex;
                  const formattedHour = hour % 12 || 12;
                  const amPm = hour < 12 ? 'AM' : 'PM';
                  return `${formattedHour}:00 ${amPm}`;
                }
                return tooltipItems[0].label;
              }
            }
          },
          legend: {
            display: false
          }
        },
        scales: {
          x: {
            title: {
              display: !!xLabel,
              text: xLabel
            },
            grid: {
              color: '#ddd'
            },
            ticks: {
              maxRotation: 0,
              autoSkip: true,
              autoSkipPadding: 10,
              callback: function(value, index) {
                // For solar profile with 24 hours, show fewer labels for clarity
                if (formatXAxis && xLabels.length === 24) {
                  return index % 4 === 0 ? xLabels[index] : '';
                }
                return xLabels[index];
              }
            }
          },
          y: {
            title: {
              display: !!yLabel,
              text: yLabel
            },
            beginAtZero: true,
            grid: {
              color: '#ddd'
            }
          }
        }
      }
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data, title, xLabel, yLabel, lineColor, formatXAxis]);

  return (
    <div style={{ height, width: '100%', marginBottom: '15px' }}>
      <canvas ref={chartRef} />
    </div>
  );
};

export default LineChartBasic; 